import React, { useContext, useEffect, useRef, useState } from 'react';
import { DeckContext } from '../../context/DeckContext';
import {
	Button,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import getBlock from '../../../../../../utils/getBlock';
import getParentBlock from '../../../../../../utils/getParentBlock';
import BlockType from '../../../../../../enums/blockType';
import getDefautlStair, {
	getDefaultStairV2,
} from '../../utils/getDefaultStair';
import getDefautlLanding, {
	getDefaultLandingV2,
} from '../../utils/getDefaultLanding';
import {
	DEPTH_COEFFICIENT,
	STEPS_COEFFICIENT,
} from '../../../../../../constants/coefficients';
import { toastError } from '../../../../../../Toaster/Toast';

import updateChildBlocks from '../../utils/updateChildBlocks';
import { Block } from '../../../../../../types/shared/blocks';
import {
	MIN_STAIR_VIABLE_BLOCK_HEIGHT,
	MAX_STAIR_HEIGHT,
} from '../../../../../../constants/limits';

import DeckBoxEdgePopper from './DeckBoxEdgePopper';
import LandingEdgePopper from './LandingEdgePopper';
import StairEdgePopper from './StairEdgePopper';
import BumpoutEdgePopper from './BumpoutEdgePopper';
import ArrowPopper from './ArrowPopper';

import EdgeLabel from 'src/enums/edgeLabel';

const EdgeModal: React.FC = () => {
	const { state, dispatch, transformWrapperRef } = useContext(DeckContext);
	const theme = useTheme();
	const [open, setOpen] = useState(true);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);

	const selectedEdge = state.selectedEdge;

	const block = getBlock(state.blocks, selectedEdge?.blockId);

	const blockEdge = block?.edges.find(
		(edge) => edge.label == selectedEdge.edgeLabel
	);

	const [newDimensions, setNewDimensions] = useState(block?.dimensions);
	const [inputEdge, setInputEdge] = useState(blockEdge?.type);
	const [editHeightLocked] = useState(true);
	const [originalDeckBoxPosition, setOriginalDeckBoxPosition] = useState<{
		x: number;
		y: number;
	} | null>(null);

	const animationInProgress = useRef(false);
	const currentBlockId = useRef<string | null>(null);
	const currentEdgeLabel = useRef<EdgeLabel | null>(null);

	const anchorEl = block?.ref?.current;

	useEffect(() => {
		if (!block) return;

		dispatch({
			type: 'UPDATE_BLOCK',
			payload: {
				...block,
				edges: block.edges.map((edge) => {
					if (edge.label == selectedEdge.edgeLabel && inputEdge) {
						return { ...edge, type: inputEdge };
					}
					return edge;
				}),
			},
		});
	}, [inputEdge]);

	useEffect(() => {
		const blockIdChanged = currentBlockId.current !== block?.id;
		const edgeLabelChanged =
			currentEdgeLabel.current !== selectedEdge?.edgeLabel;

		if (animationInProgress.current && !blockIdChanged && !edgeLabelChanged) {
			return;
		}

		currentBlockId.current = block?.id || null;
		currentEdgeLabel.current = selectedEdge?.edgeLabel || null;
		animationInProgress.current = true;

		setOpen(true);
		setNewDimensions(block?.dimensions);
		setInputEdge(blockEdge?.type);

		if (block?.ref?.current && state.canvasBounds) {
			setTimeout(() => {
				const canvasContainer = document.getElementById('main-canvas');
				if (!canvasContainer) return;

				const MODAL_WIDTH = 350;
				const MODAL_HEIGHT = 250;
				const PADDING = 10;
				const EXTRA_SPACE = 1;

				const blockCanvasX =
					(block.position?.x || 0) + state.canvasBounds.offsetX;
				const blockCanvasY =
					(block.position?.y || 0) + state.canvasBounds.offsetY;
				const blockWidth = block.displayDimensions.width * state.scaleFactor;
				const blockHeight = block.displayDimensions.height * state.scaleFactor;

				const viewportWidth = canvasContainer.clientWidth;
				const viewportHeight = canvasContainer.clientHeight;

				const edgeLabel = selectedEdge?.edgeLabel;

				let parentDeckBoxId = null;

				if (block.type === BlockType.BUMPOUT) {
					const parentBox = state.blocks.find(
						(b) =>
							b.type === BlockType.DECKBOX &&
							b.bumpouts?.some((bump) => bump.id === block.id)
					);
					parentDeckBoxId = parentBox?.id;
				} else {
					const result = getParentBlock(
						state.blocks,
						block.parentBlock || { id: block.id, type: block.type }
					);
					parentDeckBoxId = result.parentDeckBoxId;
				}

				const deckBox = state.blocks.find(
					(b) => b.id === (parentDeckBoxId || block.id)
				);

				if (edgeLabel === 'Right') {
					// No scroll or animation for RIGHT edge
					animationInProgress.current = false;
				} else if (edgeLabel === 'Left') {
					const modalLeftEdge = blockCanvasX - MODAL_WIDTH - PADDING;

					if (modalLeftEdge < 0 && deckBox) {
						setOriginalDeckBoxPosition({
							x: deckBox.position.x,
							y: deckBox.position.y,
						});

						const moveDistance = Math.abs(modalLeftEdge) + EXTRA_SPACE;

						const startX = deckBox.position.x;
						const startY = deckBox.position.y;
						const targetX = startX + moveDistance;
						const targetY = startY;
						const duration = 500;
						const startTime = performance.now();

						const easeOutQuad = (t: number) => t * (2 - t);

						const animate = (currentTime: number) => {
							const elapsed = currentTime - startTime;
							const progress = Math.min(elapsed / duration, 1);
							const easedProgress = easeOutQuad(progress);

							const currentX = startX + (targetX - startX) * easedProgress;
							const currentY = startY + (targetY - startY) * easedProgress;

							const deltaX = currentX - startX;
							const deltaY = currentY - startY;

							const updatedDeckBox = {
								...deckBox,
								position: {
									x: currentX,
									y: currentY,
								},
								// Move landings
								...(deckBox.landings?.length && {
									landings: deckBox.landings.map((landing) => ({
										...landing,
										position: {
											x:
												(landing.position?.x || 0) -
												(deckBox.position.x - startX) +
												deltaX,
											y:
												(landing.position?.y || 0) -
												(deckBox.position.y - startY) +
												deltaY,
										},
									})),
								}),
								// Move stairs
								...(deckBox.stairs?.length && {
									stairs: deckBox.stairs.map((stair) => ({
										...stair,
										position: {
											x:
												(stair.position?.x || 0) -
												(deckBox.position.x - startX) +
												deltaX,
											y:
												(stair.position?.y || 0) -
												(deckBox.position.y - startY) +
												deltaY,
										},
									})),
								}),
								// Move bumpouts
								...(deckBox.bumpouts?.length && {
									bumpouts: deckBox.bumpouts.map((bumpout) => ({
										...bumpout,
										position: {
											x:
												(bumpout.position?.x || 0) -
												(deckBox.position.x - startX) +
												deltaX,
											y:
												(bumpout.position?.y || 0) -
												(deckBox.position.y - startY) +
												deltaY,
										},
									})),
								}),
							};

							dispatch({
								type: 'UPDATE_BLOCK',
								payload: updatedDeckBox,
							});

							// Continue animation if not complete
							if (progress < 1) {
								requestAnimationFrame(animate);
							} else {
								animationInProgress.current = false; // Reset flag when animation completes
							}
						};

						requestAnimationFrame(animate);
					} else {
						animationInProgress.current = false; // No animation needed
					}
				} else if (edgeLabel === 'Top') {
					const modalTopEdge = blockCanvasY - MODAL_HEIGHT - PADDING;

					if (modalTopEdge < 0 && deckBox) {
						setOriginalDeckBoxPosition({
							x: deckBox.position.x,
							y: deckBox.position.y,
						});

						const moveDistance = Math.abs(modalTopEdge) + EXTRA_SPACE;

						// Custom smooth animation using requestAnimationFrame
						const startX = deckBox.position.x;
						const startY = deckBox.position.y;
						const targetX = startX;
						const targetY = startY + moveDistance;
						const duration = 500; // ms
						const startTime = performance.now();

						// Easing function matching scroll behavior
						const easeOutQuad = (t: number) => t * (2 - t);

						const animate = (currentTime: number) => {
							const elapsed = currentTime - startTime;
							const progress = Math.min(elapsed / duration, 1);
							const easedProgress = easeOutQuad(progress);

							// Interpolate position
							const currentX = startX + (targetX - startX) * easedProgress;
							const currentY = startY + (targetY - startY) * easedProgress;

							// Calculate deltas from original position
							const deltaX = currentX - startX;
							const deltaY = currentY - startY;

							// Update DeckBox and move all children by the same delta
							const updatedDeckBox = {
								...deckBox,
								position: {
									x: currentX,
									y: currentY,
								},
								// Move landings
								...(deckBox.landings?.length && {
									landings: deckBox.landings.map((landing) => ({
										...landing,
										position: {
											x:
												(landing.position?.x || 0) -
												(deckBox.position.x - startX) +
												deltaX,
											y:
												(landing.position?.y || 0) -
												(deckBox.position.y - startY) +
												deltaY,
										},
									})),
								}),
								// Move stairs
								...(deckBox.stairs?.length && {
									stairs: deckBox.stairs.map((stair) => ({
										...stair,
										position: {
											x:
												(stair.position?.x || 0) -
												(deckBox.position.x - startX) +
												deltaX,
											y:
												(stair.position?.y || 0) -
												(deckBox.position.y - startY) +
												deltaY,
										},
									})),
								}),
								// Move bumpouts
								...(deckBox.bumpouts?.length && {
									bumpouts: deckBox.bumpouts.map((bumpout) => ({
										...bumpout,
										position: {
											x:
												(bumpout.position?.x || 0) -
												(deckBox.position.x - startX) +
												deltaX,
											y:
												(bumpout.position?.y || 0) -
												(deckBox.position.y - startY) +
												deltaY,
										},
									})),
								}),
							};

							dispatch({
								type: 'UPDATE_BLOCK',
								payload: updatedDeckBox,
							});

							// Continue animation if not complete
							if (progress < 1) {
								requestAnimationFrame(animate);
							} else {
								animationInProgress.current = false; // Reset flag when animation completes
							}
						};

						requestAnimationFrame(animate);
					} else {
						animationInProgress.current = false; // No animation needed
					}
				} else if (edgeLabel === 'Bottom') {
					// No scroll or animation for BOTTOM edge
					animationInProgress.current = false;
				}
			}, 50);
		}
	}, [
		block?.id,
		selectedEdge?.blockId,
		selectedEdge?.edgeLabel,
		state.canvasBounds,
	]);

	const handleCloseDialog = () => {
		// Restore DeckBox to original position if it was moved
		if (originalDeckBoxPosition) {
			const { parentDeckBoxId } = getParentBlock(
				state.blocks,
				block.parentBlock || { id: block.id, type: block.type }
			);
			const deckBox = state.blocks.find(
				(b) => b.id === (parentDeckBoxId || block.id)
			);

			if (deckBox) {
				console.log('[EdgeModal] Restoring DeckBox to original position:', {
					from: deckBox.position,
					to: originalDeckBoxPosition,
				});

				// Custom smooth animation using requestAnimationFrame
				const startX = deckBox.position.x;
				const startY = deckBox.position.y;
				const targetX = originalDeckBoxPosition.x;
				const targetY = originalDeckBoxPosition.y;
				const duration = 500; // milliseconds
				const startTime = performance.now();

				// Easing function for smooth deceleration (matching scroll behavior)
				const easeOutQuad = (t: number) => t * (2 - t);

				const animate = (currentTime: number) => {
					const elapsed = currentTime - startTime;
					const progress = Math.min(elapsed / duration, 1);
					const easedProgress = easeOutQuad(progress);

					// Calculate current position based on eased progress
					const currentX = startX + (targetX - startX) * easedProgress;
					const currentY = startY + (targetY - startY) * easedProgress;

					// Calculate delta for current frame
					const deltaX = currentX - deckBox.position.x;
					const deltaY = currentY - deckBox.position.y;

					// Update DeckBox and move all children by the same delta
					const updatedDeckBox = {
						...deckBox,
						position: { x: currentX, y: currentY },
						// Move landings
						...(deckBox.landings?.length && {
							landings: deckBox.landings.map((landing) => ({
								...landing,
								position: {
									x: (landing.position?.x || 0) + deltaX,
									y: (landing.position?.y || 0) + deltaY,
								},
							})),
						}),
						// Move stairs
						...(deckBox.stairs?.length && {
							stairs: deckBox.stairs.map((stair) => ({
								...stair,
								position: {
									x: (stair.position?.x || 0) + deltaX,
									y: (stair.position?.y || 0) + deltaY,
								},
							})),
						}),
						// Move bumpouts
						...(deckBox.bumpouts?.length && {
							bumpouts: deckBox.bumpouts.map((bumpout) => ({
								...bumpout,
								position: {
									x: (bumpout.position?.x || 0) + deltaX,
									y: (bumpout.position?.y || 0) + deltaY,
								},
							})),
						}),
					};

					dispatch({
						type: 'UPDATE_BLOCK',
						payload: updatedDeckBox,
					});

					// Continue animation if not complete
					if (progress < 1) {
						requestAnimationFrame(animate);
					} else {
						animationInProgress.current = false; // Reset flag when restoration completes
					}
				};

				requestAnimationFrame(animate);
			}

			setOriginalDeckBoxPosition(null);
		}

		animationInProgress.current = false;
		currentBlockId.current = null;
		currentEdgeLabel.current = null;

		dispatch({
			type: 'SELECT_EDGE',
			payload: { blockId: null, edgeLabel: null },
		});
		dispatch({ type: 'CLEAR_SELECTION' });
		setOpen(false);
	};

	const handleAddStair = () => {
		if (!block) return;

		if (block.dimensions?.height <= MIN_STAIR_VIABLE_BLOCK_HEIGHT) {
			toastError({
				title: 'Deckbox height cannot accommodate the stair',
				description: `You can't add stair to a Deckbox with height less than or equal to ${MIN_STAIR_VIABLE_BLOCK_HEIGHT} inches.`,
			});
			return;
		}

		const currentBlockEdge = block?.edges.find(
			(edge) => edge.label == selectedEdge.edgeLabel
		);

		const stairData = getDefautlStair(block, currentBlockEdge);

		if (stairData?.defaultStair) {
			dispatch({
				type: 'ADD_STAIR',
				payload: stairData.defaultStair,
			});
		}

		if (stairData?.addIntermediateLanding) {
			const landing = getDefaultLandingV2({
				parentBlockId: stairData.defaultStair.id,
				parentBlockType: BlockType.STAIR,
				height: stairData.defaultStair.dimensions.endHeight,
				parentBlockDisplayHeight:
					stairData.defaultStair.displayDimensions.height,
				parentBlockDisplayWidth: stairData.defaultStair.displayDimensions.width,
				blockEdge: currentBlockEdge,
			});

			if (landing) {
				dispatch({
					type: 'ADD_LANDING',
					payload: landing,
				});

				const secondStair = getDefaultStairV2({
					parentBlockId: landing.id,
					parentBlockType: BlockType.LANDING,
					startHeight: landing.dimensions.height,
					parentBlockDisplayHeight: landing.displayDimensions.height,
					parentBlockDisplayWidth: landing.displayDimensions.width,
					blockEdge: currentBlockEdge,
				});

				if (secondStair) {
					dispatch({
						type: 'ADD_STAIR',
						payload: secondStair,
					});
				}
			}
		}

		setOriginalDeckBoxPosition(null);

		animationInProgress.current = false;
		currentBlockId.current = null;
		currentEdgeLabel.current = null;

		dispatch({
			type: 'SELECT_EDGE',
			payload: { blockId: null, edgeLabel: null },
		});
		dispatch({ type: 'CLEAR_SELECTION' });
		setOpen(false);
	};

	const handleAddLanding = () => {
		if (!block) return;

		if (
			block?.type == BlockType.DECKBOX &&
			block?.dimensions?.height <= MIN_STAIR_VIABLE_BLOCK_HEIGHT
		) {
			toastError({
				title: 'Deckbox height cannot accommodate landing',
				description: `You can't add landing to a Deckbox with height less than or equal to ${MIN_STAIR_VIABLE_BLOCK_HEIGHT} inches.`,
			});
			return;
		}

		const currentBlockEdge = block?.edges.find(
			(edge) => edge.label == selectedEdge.edgeLabel
		);

		const defaultLanding = getDefautlLanding(block, currentBlockEdge);

		if (defaultLanding) {
			dispatch({
				type: 'ADD_LANDING',
				payload: defaultLanding,
			});
		}

		setOriginalDeckBoxPosition(null);

		animationInProgress.current = false;
		currentBlockId.current = null;
		currentEdgeLabel.current = null;

		dispatch({
			type: 'SELECT_EDGE',
			payload: { blockId: null, edgeLabel: null },
		});
		dispatch({ type: 'CLEAR_SELECTION' });
		setOpen(false);
	};

	const handleStartHeightChange = (inches) => {
		const startHeight = inches;
		const height = startHeight - (block?.dimensions.endHeight || 0);
		const numberOfSteps = Math.ceil(height / STEPS_COEFFICIENT);
		const depth = numberOfSteps * DEPTH_COEFFICIENT;

		const updatedDimensions = {
			width: block?.dimensions.width || 0,
			startHeight,
			height,
			numberOfSteps,
			depth,
			endHeight: block?.dimensions.endHeight || 0,
		};
		setNewDimensions(updatedDimensions);

		const updatedBlock: Block = {
			...block,
			edges: block?.edges.map((edge) =>
				edge.label == selectedEdge.edgeLabel
					? { ...edge, type: inputEdge }
					: edge
			),
			dimensions: updatedDimensions,
		};

		dispatch({
			type: 'UPDATE_BLOCK',
			payload: updatedBlock,
		});

		updateChildBlocks({
			blocks: state.blocks,
			oldBlock: block,
			updatedBlock,
			dispatch,
		});
	};

	const handleEndHeightChange = (inches) => {
		const endHeight = inches;
		const height = (block?.dimensions.startHeight || 0) - endHeight;
		const numberOfSteps = Math.ceil(height / STEPS_COEFFICIENT);
		const depth = numberOfSteps * DEPTH_COEFFICIENT;

		const updatedDimensions = {
			width: block?.dimensions.width || 0,
			startHeight: block?.dimensions.startHeight || 0,
			height,
			numberOfSteps,
			depth,
			endHeight,
		};
		setNewDimensions(updatedDimensions);

		const updatedBlock: Block = {
			...block,
			edges: block?.edges.map((edge) =>
				edge.label == selectedEdge.edgeLabel
					? { ...edge, type: inputEdge }
					: edge
			),
			dimensions: updatedDimensions,
		};

		dispatch({
			type: 'UPDATE_BLOCK',
			payload: updatedBlock,
		});

		updateChildBlocks({
			blocks: state.blocks,
			oldBlock: block,
			updatedBlock,
			dispatch,
		});
	};

	const handleSave = () => {
		// Validation checks
		if (block?.type == BlockType.STAIR) {
			const parentBlock = getBlock(state.blocks, block?.parentBlock.id);

			if (newDimensions?.startHeight != parentBlock?.dimensions.height) {
				toastError({
					title: 'Block height not matching',
					description: `${block.label} start height should be the same as ${parentBlock?.label} height`,
				});
				return;
			}

			if (newDimensions?.startHeight <= newDimensions?.endHeight) {
				toastError({
					title: 'Stair height is incorrect',
					description:
						'Stair start height should be greater than its Landing height',
				});
				return;
			}

			if (newDimensions?.height > MAX_STAIR_HEIGHT) {
				toastError({
					title: 'Stair height is incorrect',
					description: `Stair height cannot be higher than ${MAX_STAIR_HEIGHT / 12}ft`,
				});
				return;
			}
		}

		// All changes are already dispatched and auto-saved
		// Just close the dialog
		handleCloseDialog();
	};

	// Render edge-specific content based on block type
	const renderEdgeContent = () => {
		if (!block || !blockEdge) return null;

		switch (block.type) {
			case BlockType.DECKBOX:
				return (
					<DeckBoxEdgePopper
						block={block}
						blockEdge={blockEdge}
						inputEdge={inputEdge}
						setInputEdge={setInputEdge}
						onAddLanding={handleAddLanding}
						onAddStair={handleAddStair}
						onDropdownOpenChange={setIsDropdownOpen}
					/>
				);
			case BlockType.LANDING:
				return (
					<LandingEdgePopper
						block={block}
						blockEdge={blockEdge}
						inputEdge={inputEdge}
						setInputEdge={setInputEdge}
						onAddStair={handleAddStair}
						onDropdownOpenChange={setIsDropdownOpen}
					/>
				);
			case BlockType.STAIR:
				return (
					<StairEdgePopper
						block={block}
						blockEdge={blockEdge}
						inputEdge={inputEdge}
						setInputEdge={setInputEdge}
						newDimensions={newDimensions}
						onStartHeightChange={handleStartHeightChange}
						onEndHeightChange={handleEndHeightChange}
						editHeightLocked={editHeightLocked}
						onAddLanding={handleAddLanding}
						onDropdownOpenChange={setIsDropdownOpen}
					/>
				);
			case BlockType.BUMPOUT:
				return (
					<BumpoutEdgePopper
						block={block}
						blockEdge={blockEdge}
						inputEdge={inputEdge}
						setInputEdge={setInputEdge}
						onDropdownOpenChange={setIsDropdownOpen}
					/>
				);
			default:
				return null;
		}
	};

	// let showAddLandingButton = block?.type == BlockType.DECKBOX || block?.type == BlockType.STAIR;
	//
	// if (block?.type == BlockType.STAIR) {
	// 	showAddLandingButton =
	// 		selectedEdge.edgeLabel == blockAttachedEdge?.label ||
	// 		selectedEdge.edgeLabel == getOppositeEdge(blockAttachedEdge?.label);
	// }

	return (
		<ArrowPopper
			open={open}
			anchorEl={anchorEl}
			placement={
				state.selectedEdge?.edgeLabel === EdgeLabel.TOP
					? 'top-start'
					: state.selectedEdge?.edgeLabel === EdgeLabel.BOTTOM
						? 'bottom-start'
						: state.selectedEdge?.edgeLabel === EdgeLabel.LEFT
							? 'left-start'
							: 'right-start'
			}
			onClickAway={handleCloseDialog}
			width="250px"
			zIndex={theme.zIndex.modal + 1}
			enableClickAway={!isDropdownOpen}
			edgeLabel={state.selectedEdge?.edgeLabel}
			transformWrapperRef={transformWrapperRef}
			enableCanvasPan={true}
			disablePortal={true}
		>
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="flex-start"
				sx={{ p: theme.spacing(1, 1.25), pb: theme.spacing(0.5) }}
			>
				<DialogTitle
					id="edge-modal-title"
					component="div"
					sx={{ p: 0, fontSize: '1rem', fontWeight: 600 }}
				>
					Edit {block?.label} - {selectedEdge?.edgeLabel} Edge
				</DialogTitle>
			</Stack>

			<DialogContent
				id="edge-modal-content"
				sx={{
					p: theme.spacing(1.25, 1.25),
					maxHeight: '60vh',
					overflowY: 'auto',
					'&::-webkit-scrollbar': {
						width: '0.375rem',
					},
					'&::-webkit-scrollbar-track': {
						background: theme.palette.grey[100],
					},
					'&::-webkit-scrollbar-thumb': {
						background: theme.palette.grey[400],
						borderRadius: '0.1875rem',
					},
					'&::-webkit-scrollbar-thumb:hover': {
						background: theme.palette.grey[500],
					},
				}}
			>
				{renderEdgeContent()}
			</DialogContent>

			<DialogActions
				sx={{
					borderTop: '1px solid',
					borderColor: theme.palette.divider,
					p: theme.spacing(1, 1.25),
					gap: theme.spacing(0.75),
					justifyContent: 'flex-end',
				}}
			>
				<Button
					onClick={handleCloseDialog}
					sx={{
						textTransform: 'none',
						color: theme.palette.text.primary,
					}}
				>
					Cancel
				</Button>
				<Button
					onClick={handleSave}
					variant="contained"
					sx={{ textTransform: 'none' }}
				>
					Save
				</Button>
			</DialogActions>
		</ArrowPopper>
	);
};

export default EdgeModal;
